<script lang="ts">
  import type { BlogFrontPageStoryblok, BlogPostStoryblok } from '~/component-types-sb'; // TODO: probably should include this in tsconfig types instead (it is a d.ts file)

  import { page } from '$app/state';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';

  import { getStoryblokApi, storyblokEditable } from '@storyblok/svelte';
  import { JsonLd } from '@agapi/ui/json-ld';
  import { css } from '@agapi/ui/panda/css';
  import { Button } from '@agapi/ui/button';
  import { Loader } from '@agapi/ui/loader';
  import { Section } from '@agapi/ui/section';
  import { Breadcrumb, BreadcrumbItem } from '@agapi/ui/breadcrumb';
  import * as m from "$lib/parachute/messages";

  import type { BlogCardProps } from '../../../../components';
  import { BlogCard } from '../../../../components';
  import { blogPageStyles } from '../blog.styles';
  import { calculateReadingTime, parseIngress } from '../../../../utils/storyblok.client';
  import { rewriteUrl } from '../../../../utils/locale.shared';

  const { blok, posts, total, perPage, currentPage, categories }: {
    blok: BlogFrontPageStoryblok;
    posts: any[];
    total: number;
    perPage: number;
    currentPage: number;
    categories: any;
  } = $props();

  let allPosts = $state(posts);
  let loading = $state(false);
  let currentPageState = $state(currentPage);

  const loadMore = async () => {
    if (loading || allPosts?.length >= total) { return };

    loading = true;
    currentPageState += 1;
    const storyblokApi = getStoryblokApi();

    try {
      const { data } = await storyblokApi.get('cdn/stories', {
        version: 'published',
        starts_with: 'happy-green/magazine/posts/',
        is_startpage: false,
        per_page: perPage,
        page: currentPageState
      });

      allPosts = [...allPosts, ...data.stories];
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      loading = false;
    }
  };

  const blogCardProps = (post: BlogPostStoryblok) => ({
    title: post.name,
    description: parseIngress(post.content.body.content),
    readingTime: calculateReadingTime(post.content.body.content),
    imageUrl: post.content.image?.filename,
    imageAlt: post.content.image?.alt,
    slug: post.slug,
    publishedAt: post.published_at,
    category: post.content.category[0]?.name || ''
  }) satisfies BlogCardProps;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: blok.heading,
    description: blok.sub_title,
    url: '${page.url.origin}/magazine',
    blogPost: posts.map(post => ({
      '@type': 'BlogPosting',
      headline: post.name,
      url: `${page.url.origin}/magazine/${post.slug}`,
      datePublished: post.published_at,
      author: {
        '@type': 'Person',
        name: post.content.author || PUBLIC_BRAND_NAME
      }
    }))
  };

  const { section, sectionContainer, heading, subTitle, nav, postGrid, loadMoreContainer } = blogPageStyles.raw();
</script>

<svelte:head>
  <title>{blok.meta_title || `${PUBLIC_BRAND_NAME}`}</title>
  <meta name="description" content={blok.meta_description || blok.sub_title} />
  <meta property="og:title" content={blok.meta_title || `${PUBLIC_BRAND_NAME}`} />
  <meta property="og:description" content={blok.meta_description || blok.sub_title} />
  <meta property="og:image" content={blok.meta_image?.filename} />
  <meta property="og:url" content="${page.url.origin}/blog" />
  <meta property="og:type" content="website" />
</svelte:head>

<JsonLd data={schemaData} />

<Section as="header" rootStyles={section} containerStyles={sectionContainer} use={[[storyblokEditable, blok]]}>
  <Breadcrumb>
    <BreadcrumbItem index={0}>
      {m.blog()}
    </BreadcrumbItem>
  </Breadcrumb>
  <h1 class={css(heading)}>{blok.heading}</h1>
  <p class={css(subTitle)}>{blok.sub_title}</p>
  <nav class={css(nav)}>
    {#each categories as category}
      <Button variant="secondary" as="a" size="sm" href={rewriteUrl(page, `/blog/${category.slug}`)}>
        {category.name}
    </Button>
    {/each}
  </nav>
</Section>
<Section rootStyles={section} containerStyles={sectionContainer} use={[[storyblokEditable, blok]]}>
  <div class={css(postGrid)}>
    {#each allPosts as post}
      <BlogCard {...blogCardProps(post)} />
    {/each}
  </div>

  {#if allPosts?.length < total}
    <div class={css(loadMoreContainer)}>
      <Button onclick={loadMore} disabled={loading}>
        {loading ? m.loading() : m.loadMoreItems()}
        {#if loading}
          <Loader />
        {/if}
      </Button>
    </div>
  {/if}
</Section>
